import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { Grid } from "components/Grid";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { UserManagementApiUrl } from "environment/routeSettings";
import Spinner from "components/Spinner";
import { DataGridRef } from "devextreme-react/data-grid";
import { useRegion_v2 } from "contexts/regions_v2";
import { TUser, UserType } from "types/dto";
import { TDataTableColumn } from "components/Grid/types";
import { Enum2Array } from "utils/enumTools";
import { TUsersGridRow } from "components/Grid/grids/UsersGrid/types";
import { TransparentLoading } from "components/TransparentLoading";

type TProps = {
  data: TUser[];
  actionButtons?: {
    icon: string;
    hint: string;
    onClick: (row: any) => void;
  }[];
  isLoading?: boolean;
  onRowClick?: (e: TUsersGridRow) => void;
  /**
   * Adds right-click -> "view details" / "open in new tab" on each row.
   * Opt-in for consistency with the other entity grids, which are also
   * rendered inside chooser modals where navigating away is wrong.
   */
  enableDetailsContextMenu?: boolean;
};
const UsersGrid = forwardRef<DataGridRef, TProps>((props, ref) => {
  const {
    data,
    isLoading,
    actionButtons,
    onRowClick,
    enableDetailsContextMenu,
  } = props;

  // Called unconditionally (rules of hooks); the prop only decides whether
  // the resulting menu is handed to the grid.
  const detailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/users/details/${row.id}` : undefined
  );
  const { t } = useTranslation();
  const { allJurisdictions, allCities, allProvinces } = useRegion_v2();

  // const dataSource = useMemo(() => {
  //   return new CustomStore({
  //     key: "id",
  //     load: async (loadOption) => {
  //       if (loadOption.filter) {
  //         loadOption.filter = generateFiltersByAcceptingCustomStringInput(
  //           loadOption.filter
  //         );
  //       }
  //
  //       return (await netCall({
  //         method: "POST",
  //         endPoint: `${ManagementApiUrl()}/api/Apartment/GetApartments`,
  //         data: {
  //           payloadData: {
  //             loadOptions: { ...loadOption, requireTotalCount: false },
  //             provinceId: provinceId,
  //             cityId: cityId,
  //             jurisdictionId: jurisdictionId,
  //           },
  //         },
  //       })) as LazyLoadingRequest;
  //     },
  //   });
  // }, [provinceId, cityId, jurisdictionId]);

  const columns = useMemo(() => {
    const _columns: TDataTableColumn[] = [
      {
        dataField: "id",
        caption: t("id"),
      },
      {
        dataField: "username",
        caption: t("username"),
      },
      {
        dataField: "firstName",
        caption: t("firstName"),
      },
      {
        dataField: "lastName",
        caption: t("lastName"),
      },
      {
        dataField: "email",
        caption: t("email"),
      },
      {
        dataField: "provinceId",
        caption: t("province"),
        lookup: {
          dataSource: allProvinces,
          valueExpr: "id",
          displayExpr: "name",
        },
      },
      {
        dataField: "cityId",
        caption: t("city"),
        lookup: {
          dataSource: allCities,
          valueExpr: "id",
          displayExpr: "name",
        },
      },
      {
        dataField: "jurisdictionId",
        caption: t("jurisdiction"),
        lookup: {
          dataSource: allJurisdictions,
          valueExpr: "id",
          displayExpr: "name",
        },
      },
      {
        dataField: "userType",
        caption: t("userType"),
        lookup: {
          dataSource: Enum2Array(UserType).map((item) => ({
            id: item.value,
            name: t(item.name),
          })),
          valueExpr: "id",
          displayExpr: "name",
        },
      },
    ];
    if (actionButtons && actionButtons.length > 0) {
      _columns.push({
        caption: t("actions"),
        visible: true,
        width: 110,
        buttons: actionButtons.map((button) => ({
          icon: button.icon,
          hint: button.hint,
          onClick: (e: any) => button.onClick(e.row.data),
        })),
      });
    }
    return _columns;
  }, [t, actionButtons]);

  return (
    <>
      <div className={"w-full flex gap-x flex-1 min-h-0"}>
        {isLoading && <TransparentLoading />}
        <Grid
          ref={ref}
          gridKey={"id"}
          dataSource={data}
          columns={columns}
          rowContextMenu={
            enableDetailsContextMenu ? detailsContextMenu : undefined
          }
          hasSearchPanel={true}
          hasPagination={true}
          hasGroupingFeatures={false}
          hasFilterBuilder={true}
          onRowClick={(e) => {
            e?.data && onRowClick?.(e.data as TUsersGridRow);
          }}
          height={"100%"}
        />
      </div>
    </>
  );
});

export default UsersGrid;
